import { Injectable } from '@angular/core';
import { format, fromUnixTime, parse } from 'date-fns';

import { environment } from '../../../environments/environment';
import { StorageService } from '@services/common/storage.service';
import { TimeService } from '@services/common/time.service';
import { PlansEnum } from '@shared/components/select-plan/select-plan/plan-mapper.service';
import { IUser } from '@shared/interfaces/entity/user.interface';

@Injectable({
  providedIn: 'root'
})
export class SegmentService {
  private readonly _analytics: object;

  isIdentify = false;

  constructor(
    private _storage: StorageService,
    private _timeService: TimeService
  ) {
    this._analytics = window['analytics'];
  }

  createUpdatedUser({ data, auth }: { data?: any; auth?: any }): IUser {
    const user: any = data?.user || auth.currentUser;

    if (!user.account && !user.subscription) {
      const { account, ...restOfSubscription } = data.subscription || auth.currentSubscription;
      const currentCurrency = data.currency || auth.currentCurrency;

      return {
        ...user,
        account,
        subscription: {
          ...restOfSubscription,
          currency: currentCurrency
        }
      };
    }

    return user;
  }

  identify(user: any): void {
    if (!this.validate()) {
      return;
    }

    if (user && !this.isIdentify) {
      // @ts-ignore
      window.analytics.identify(user.id, {
        name: user.first_name + ' ' + user.last_name,
        email: user.email,
        status: user.status,
        phone: user.phone,
        svenn_role: user.role,
        signed_up_at: user.account.created_at,

        firstname: user.first_name,
        lastname: user.last_name,
        company_name: user.account ? user.account.name : '',
        plan: user.subscription && user.subscription.plan ? user.subscription.plan.display_name : '',
        current_licenses: user.subscription ? user.subscription?.quantity : '',
        package_price: user.subscription ? user.subscription?.package_price : '',
        license_price: user.subscription ? user.subscription?.license_price : '',
        enterprise_discount: user.subscription ? user.subscription?.discount : '',
        currency: user.subscription && user.subscription.currency ? user.subscription.currency.iso : '',
        monthly_spend: user.subscription ? user.subscription?.mrr : '',
        renewal: this.formatSubscriptionEndDate(user.subscription),
        churned_date: this.formatChurnedDate(user.account)
      });

      this.isIdentify = true;
    }
  }

  page(page): void {
    if (!this.validate()) {
      return;
    }

    if (page) {
      // @ts-ignore
      window.analytics.page(page);
    }
  }

  alias(userId): void {
    if (!this.validate()) {
      return;
    }

    if (userId) {
      // @ts-ignore
      window.analytics.alias(userId);
    }
  }

  group(subscription = null): void {
    if (!this.validate()) {
      return;
    }

    if (subscription) {
      // @ts-ignore
      window.analytics.group(subscription.account.id, {
        company_id: subscription.account.id,
        name: subscription.account.name,
        contact_person_email: subscription.account?.contact_person_email,
        remote_created_at: subscription.account?.created_at,
        status: subscription.account?.status,
        plan: subscription.plan ? subscription.plan?.display_name : '',
        renewal:
          subscription.plan && subscription.plan?.name !== 'Trial' && subscription.plan?.name !== 'Free'
            ? this._timeService.timeStampToDate(subscription?.period_end)
            : null,
        trial_due:
          subscription.plan && subscription.plan?.name === 'Trial'
            ? this._timeService.timeStampToDate(subscription?.period_end)
            : null,
        old_version: subscription.account?.is_from_old_system,
        contact_permission: subscription.account?.is_contact_allowed,
        company_size: subscription.account?.company_size,
        address: subscription.account?.address,
        phone: subscription.account?.phone,
        industry: subscription.account?.industry_id,
        org_number: subscription.account?.org_number,
        current_licenses: subscription?.quantity,
        billing_interval: subscription?.period,
        license_price: subscription?.price
      });
    }
  }

  track(name, data = {}): void {
    if (!this.validate()) {
      return;
    }

    const user = this._storage.getItem('user');
    const subscription = this._storage.getItem('subscription');

    if (subscription?.account_id) {
      data['company_id'] = subscription?.account_id;
    }

    const options = subscription?.account_id ? { context: { groupId: subscription?.account_id.toString() } } : {};
    // @ts-ignore
    return window.analytics.track(name, data, options, {});
  }

  reset(): void {
    if (!this.validate()) {
      return;
    }
    // @ts-ignore
    window.analytics.reset();
  }

  identifyUser(user): void {
    this.checkIfExistAnalytics(user);
  }

  private checkIfExistAnalytics(user): void {
    // @ts-ignore
    if (window.analytics) {
      // @ts-ignore
      this.identify(user);
    } else {
      setTimeout(() => {
        this.identifyUser(user);
      }, 500);
    }
  }

  private validate(): boolean {
    return !!environment.SEGMENT_TOKEN && window['analytics'];
  }

  private formatSubscriptionEndDate(subscription: any): string | null {
    if (subscription.plan && subscription.plan.name !== PlansEnum.Trial && subscription.plan.name !== PlansEnum.Free) {
      const date: Date = fromUnixTime(subscription.period_end);
      return format(date, 'yyyy-MM-dd');
    }

    return null;
  }

  private formatChurnedDate(account: any): string {
    const churnedDate: string = account?.churned_date;

    if (churnedDate !== null) {
      const formattedChurnedDate: Date = parse(churnedDate, 'yyyy-MM-dd HH:mm:ss', new Date());
      return format(formattedChurnedDate, 'yyyy-MM-dd');
    }

    return '';
  }
}
